/**
 * TriangleIdentitiesExplainer — the two coherence laws that pin an
 * adjunction's unit and counit to its underlying functors.
 *
 * The triangle identities turn "F and G are mirror images" from a slogan
 * into a structural fact: going `F → FGF → F` via the unit and counit must
 * be the identity on `F`, and the corresponding `G → GFG → G` round trip
 * must be the identity on `G`. CSK uses this coherence as the algebraic
 * record that a build-step and an observe-step really do recover each
 * other — there is no "leaky" build-then-observe and no "phantom"
 * observe-then-build.
 *
 * Includes an interactive hover legend so the reader can match each leg
 * of the triangle to its concrete role.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useState } from "react";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, FONT, GRAY } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  TriangleIdentityDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
  type TriangleIdentityLeg,
} from "../shared/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.ALWAYS.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-triangle-identities",
  kind: "Categorical structure",
  title: "Triangle identities",
  subtitle: "(εF) ∘ (Fη) = id_F   ·   (Gε) ∘ (ηG) = id_G",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Adjunction", href: "#explainer-adjunction", accent: ACCENT },
  { label: "Adjunctions → monads", href: "#explainer-adjunctions-generate-monads", accent: ACCENT },
  { label: "Beck-Chevalley", href: "#explainer-beck-chevalley", accent: ACCENT },
  { label: "INV.CONSISTENCY (DPG)", href: "#explainer-inv-consistency" },
];

interface LegMeta {
  readonly leg: TriangleIdentityLeg;
  readonly title: string;
  readonly intuition: string;
}

// inline: local
const LEG_META: readonly LegMeta[] = [
  {
    leg: "Fη",
    title: "Build, then build's image of the build's witness",
    intuition:
      "Start at F. Apply F to the unit η, which inserts a value into the right adjoint's image. You land in FGF — the free build that has been observed by G and re-built by F.",
  },
  {
    leg: "εF",
    title: "Collapse the doubled-build on the F side",
    intuition:
      "From FGF, apply ε at the F-image to collapse F∘G back to identity. You return to F. Together with Fη above, this is exactly the round trip that must equal id_F.",
  },
  {
    leg: "ηG",
    title: "Observe, then observe's image of the observation's witness",
    intuition:
      "Start at G. Apply η at G, which inserts the right adjoint's image into the left adjoint's image. You land in GFG.",
  },
  {
    leg: "Gε",
    title: "Collapse the doubled-observation on the G side",
    intuition:
      "From GFG, apply G to the counit ε, collapsing F∘G inside G. You return to G — completing the second triangle identity.",
  },
];

function TriangleIdentityHoverDemo(): React.JSX.Element {
  const [highlighted, setHighlighted] = useState<TriangleIdentityLeg | undefined>(undefined);
  const setLeg = useCallback(
    (leg: TriangleIdentityLeg | undefined): (() => void) =>
      () => {
        setHighlighted(leg);
      },
    [],
  );
  const active = LEG_META.find((m) => m.leg === highlighted);
  return (
    <div style={{ display: "grid", gap: "0.6rem" }}>
      <TriangleIdentityDiagram accent={ACCENT} highlighted={highlighted} />
      <div
        role="group"
        aria-label="Hover any leg to see its role; both triangles together pin the adjunction"
        style={{
          display: "grid",
          gap: "0.35rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        }}
      >
        {LEG_META.map((m) => (
          <button
            key={m.leg}
            type="button"
            onMouseEnter={setLeg(m.leg)}
            onFocus={setLeg(m.leg)}
            onMouseLeave={setLeg(undefined)}
            onBlur={setLeg(undefined)}
            style={{
              appearance: "none",
              border: highlighted === m.leg ? `2px solid ${ACCENT}` : `1px solid ${GRAY[300]}`,
              background: highlighted === m.leg ? `${ACCENT}1A` : "white",
              borderRadius: "0.4rem",
              padding: "0.35rem 0.55rem",
              textAlign: "left",
              cursor: "pointer",
              fontSize: "0.75rem",
              color: GRAY[700],
              fontFamily: FONT.body,
            }}
            aria-pressed={highlighted === m.leg}
          >
            <span style={{ fontFamily: FONT.mono, fontWeight: 700, color: ACCENT }}>{m.leg}</span>
            <span style={{ marginLeft: "0.4rem" }}>{m.title}</span>
          </button>
        ))}
      </div>
      {active !== undefined && (
        <p
          style={{
            margin: 0,
            padding: "0.55rem 0.75rem",
            borderLeft: `3px solid ${ACCENT}`,
            background: `${ACCENT}10`,
            color: GRAY[700],
            fontSize: "0.85rem",
            borderRadius: "0.3rem",
          }}
        >
          <strong style={{ color: ACCENT, fontFamily: FONT.mono }}>{active.leg}.</strong>{" "}
          {active.intuition}
        </p>
      )}
    </div>
  );
}

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      An adjunction's <strong>triangle identities</strong> are the two coherence equations that say
      "going through the unit then the counit on either side returns you to where you started". They
      turn the unit and counit from "we declared them" into "they pin{" "}
      <MathInline>{tex`F`}</MathInline> and <MathInline>{tex`G`}</MathInline> to each other".
    </p>
  ),
  intuition: (
    <>
      <p>
        Imagine a translation pair between two languages — French and English, say. The{" "}
        <em>unit</em> is "every French word has an English translation"; the <em>counit</em> is
        "every English word has a French translation". Without further laws, these could be silly:
        translate a French word, then translate back, and end up with a different French word.
      </p>
      <p>
        The triangle identities forbid that silliness. They say: if you start with a French
        sentence, translate it to English in the canonical way (the unit), then translate that
        English sentence back to French via the counit, you get the original sentence — not a
        paraphrase. The same has to hold the other way.
      </p>
      <p>
        In categorical language: <MathInline>{tex`F`}</MathInline> and{" "}
        <MathInline>{tex`G`}</MathInline> are the two translations;{" "}
        <MathInline>{tex`\eta`}</MathInline> says "every <MathInline>{tex`A`}</MathInline> has an
        image <MathInline>{tex`\eta_{A} : A \to G(F A)`}</MathInline>";{" "}
        <MathInline>{tex`\varepsilon`}</MathInline> says "every <MathInline>{tex`B`}</MathInline>{" "}
        has a co-image <MathInline>{tex`\varepsilon_{B} : F(G B) \to B`}</MathInline>"; the
        triangles say the two round-trips (one starting at <MathInline>{tex`F`}</MathInline>, one
        starting at <MathInline>{tex`G`}</MathInline>) are both the identity.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`F : C \to D`}</MathInline> and{" "}
        <MathInline>{tex`G : D \to C`}</MathInline> be functors with{" "}
        <MathInline>{tex`\eta : \mathrm{Id}_{C} \Rightarrow G \circ F`}</MathInline> and{" "}
        <MathInline>{tex`\varepsilon : F \circ G \Rightarrow \mathrm{Id}_{D}`}</MathInline>. The
        pair is an adjunction <MathInline>{tex`F \dashv G`}</MathInline> iff the two{" "}
        <strong>triangle identities</strong> hold:
      </p>
      <MathBlock
        caption="Triangle identities: the two coherence laws that pin η and ε to F and G."
        ariaLabel="Triangle identities: (εF) ∘ (Fη) = id_F and (Gε) ∘ (ηG) = id_G"
      >
        {tex`(\varepsilon F) \circ (F \eta) \;=\; \mathrm{id}_{F}
\qquad
(G \varepsilon) \circ (\eta G) \;=\; \mathrm{id}_{G}`}
      </MathBlock>
      <ul>
        <li>
          At every <MathInline>{tex`A`}</MathInline> in <MathInline>{tex`C`}</MathInline>,{" "}
          <MathInline>
            {tex`\varepsilon_{F A} \circ F(\eta_{A}) \;=\; \mathrm{id}_{F A}`}
          </MathInline>
          .
        </li>
        <li>
          At every <MathInline>{tex`B`}</MathInline> in <MathInline>{tex`D`}</MathInline>,{" "}
          <MathInline>
            {tex`G(\varepsilon_{B}) \circ \eta_{G B} \;=\; \mathrm{id}_{G B}`}
          </MathInline>
          .
        </li>
      </ul>
      <TriangleIdentityHoverDemo />
      <p style={{ marginTop: "0.75rem" }}>
        These identities are equivalent to the hom-isomorphism presentation: they are exactly what
        make the canonical maps
      </p>
      <MathBlock ariaLabel="Hom_D(F A, B) maps to Hom_C(A, G B)">
        {tex`\mathrm{Hom}_{D}(F A,\, B) \;\longrightarrow\; \mathrm{Hom}_{C}(A,\, G B)`}
      </MathBlock>
      <p>
        and its inverse mutually inverse. Whisker, paste, simplify — the triangles are the bottom of
        the algebraic stack.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        In CSK, the triangle identities make the Dual Pair Guarantees <em>structural</em> rather
        than aspirational. Three concrete payoffs:
      </p>
      <ul>
        <li>
          <strong>No leaky build-step.</strong> If "establishing an invariant" is a free F-algebra
          and "preserving it" is the cofree F-coalgebra observer, the first triangle identity is the
          formal record that establishing then observing returns the same invariant — not a weakened
          relative.
        </li>
        <li>
          <strong>No phantom observation.</strong> Symmetrically, the second triangle identity
          eliminates the case where an observer reports something that the builder never built. The
          auditor's report is recoverable from the build trace by construction.
        </li>
        <li>
          <strong>Compositional auditing.</strong> Because both triangles hold on the nose,{" "}
          <em>Immunity Closure Guarantees</em> can package the adjunction's unit and counit as the
          two witnesses an auditor must produce — and the triangle identities prove that no third
          witness is needed.
        </li>
      </ul>
      <p>
        Read the triangle identities as the algebraic statement of{" "}
        <em>"nothing escapes the pair"</em>. Drop either one and you regain a vulnerability class —
        the counter-models in the INV.CONSISTENCY card are literally what happens when a triangle
        identity is removed.
      </p>
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "Triangle identities §2 and their use in the F-Algebra ⊣ F-Coalgebra adjunction.",
    },
    {
      label: "Triangle-identity proofs (Agda, MathLibCore)",
      path: "agda/MathLibCore/CategoryTheory/Adjunction.agda",
      note: "Mechanized triangle identities for the standard adjunction record.",
    },
    {
      label: "Adjunction composition (Agda)",
      path: "agda/Foundation/BeckChevalley/CategoriesLib/AdjunctionComposition.agda",
      note: "Composing adjunctions preserves both triangle identities.",
    },
    {
      label: "INV.CONSISTENCY counter-models",
      path: "agda/Contrib/StructuralInvariantSafetyGuarantees/InvariantDualPair.agda",
      note: "What happens when you drop a triangle identity: drift on one side, vacuity on the other.",
    },
  ],
};

export function TriangleIdentitiesExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<TriangleIdentityDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
